import React, {useRef, useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";

import { DropDownField, TextInputField } from "../../../helpers";
import Button from "../../../helpers/Button";
import { Menu } from 'primereact/menu';

import { fetchUsersList, updateSearchParams, updateSearchOptions, setSearchConfig, setIsSearchByLocked } from "../../../services/redux/reducers/listUser/listUser";
import { fetchStatus } from "../../../services/redux/reducers/commonThunk";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";

import { getAddFilterOptions, getMatchedRegexPattern, normalizeLabel } from "../../../utils";


const usersSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'User Data', value: 'usersData' }
        ]
    },
    {   srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text"
    },
    {
        srNO: 3,
        static: false,
        type: "SELECT",
        name: "statusId",
        label: "Status",
        options: [
            { label: 'Active', value: 1 },
            { label: 'Inactive', value: 2 }
        ]
    },
    {
        srNO: 4,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "companyName",
        label:"Company Name",
        priority: 2
    },
    {
        srNO: 5,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "userEmail",
        label:"User Email"
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "phoneNumber",
        label:"Phone Number"
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "gstin",
        label:"GSTIN"
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "userName",
        label: "User Name",
        priority: 1
    },
   
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "userRoleName",
        label: "User Role"
    }
]

const Search = (props) => {
    const dispatch = useDispatch()

    const isLoading = useSelector(state => state.users.loading)
    const paginationParams = useSelector(state => state.users.paginationParams)
    const searchParams = useSelector(state => state.users.searchParams)
    const searchOptions = useSelector(state => state.users.searchOptions)
    const isSearchByLocked = useSelector(state => state.users.isSearchByLocked)

    const filterMenu = useRef(null)
    const searchTextRef = useRef(null)

    const searchConfig = usersSearchConfig

    useEffect(()=>{
        dispatch(setSearchConfig(usersSearchConfig))
        dispatch(fetchStatus())

        if(searchOptions.length === 0){
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = {...searchParams}

        if(relatedSearchOption.type === "SEARCH_BY"){
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "DATE"){
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "SELECT"){
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        
        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }
        const updatedSearchParams = {...searchParams, [event.target.name]: eventValue, paginator: true}

        if(event.target.name === "searchText" && event.target.value.length > 3){
            const availableOptions = [...usersSearchConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            if(!isSearchByLocked){
                updatedSearchParams.searchBy = matchedPattern
            }
        }

        if (event.target.name === "searchBy"){
            dispatch(setIsSearchByLocked(true))
        }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleSearchByLock = () => {
        if(isSearchByLocked && searchParams.searchText === ""){
            const updatedSearchParams = { ...searchParams, searchBy : "all" }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        else if(isSearchByLocked && searchParams.searchText !== ""){
            const availableOptions = [...usersSearchConfig]
            const matchedPattern = getMatchedRegexPattern(searchParams.searchText, availableOptions, searchParams.searchBy)
            const updatedSearchParams = { ...searchParams, searchBy : matchedPattern }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        dispatch(setIsSearchByLocked(!isSearchByLocked))
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const isSearchByVisible = searchParams.searchText || isSearchByLocked

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    } 

    const handleSearch = () => {
        let errorFound = false

        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        const updatedSearchParams = {...searchParams}

        if(updatedSearchParams.searchBy !== "all"){
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        if(!errorFound){
            dispatch(fetchUsersList({...paginationParams, ...updatedSearchParams, pageNo: 0 }))
        }
    }

    return(
        <div className="p-card" style={{border:"1px solid #e9ecef"}} onKeyDown={handleSearchKeyPress}>
            <div className="grid"> 
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{label: '', value: 'all'}]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })

                            return (
                                <div className={`flex flex-column col-12 m-0 p-0 ${isSearchByVisible ? 'search-by-filter-set' : ''}`}>
                                    <div className={`flex flex-column col-12`}>
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    </div>
                                    {(searchParams.searchText || isSearchByLocked)  && (
                                        <>
                                            <div className="flex justify-content-end p-0 col-12 remove-opt" style={{height: '0px'}}>
                                                <i
                                                    className={`pi ${isSearchByLocked ? 'pi-lock' : 'pi-lock-open'}`}
                                                    style={{ position: 'absolute', top: '-2px', left: '70px', cursor: 'pointer', color: 'orange', zIndex: '1000' }}
                                                    onClick={() => handleSearchByLock()}
                                                ></i>
                                            </div>
                                            <div className="flex flex-column col-12" style={{marginTop: '-12px'}}>
                                                <DropDownField
                                                    name="searchBy"
                                                    labelText="Search By"
                                                    isSearchByLocked={isSearchByLocked}
                                                    handleSearchByLock={handleSearchByLock}
                                                    isLockVisible={true}
                                                    options={searchByOptions}
                                                    value={searchParams["searchBy"]}
                                                    onChange={handleSearchOnChange}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        }
                        
                        if(eachOption.type === "SELECT"){
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({label: eachSelectOption.label, value: eachSelectOption.value}))
                            return (
                                <>
                                    { !eachOption.static ? 
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={()=> removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null 
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)}></i>
                                        </div>
                                    }
                                    <div className="flex flex-column col-12">
                                        <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }
                    })
                }

                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{cursor: isLoading ? 'not-allowed' : 'pointer'}}>
                            <Button label="Search" style={{flexGrow: 1}} className="py-2" onClick={handleSearch} disabled={isLoading}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;